import dayjs from 'utils/dayjs';
import React from 'react';
import s from './s.module.less';
import { Event, EventWithTimeOverlap } from 'types/calendar';
import { getEventColor, getEventColorSetting, isCancelledEvent, isEventTimeOverlap } from 'utils/calendar';
import { Tooltip, Popover } from 'antd';
import useCalendarColors from 'hooks/useCalendarColors';
import AddAndEditCalendarEvent from 'components/AddAndEditCalendarEvent';
import { KlarityService, Prospect } from 'types/common';
import EventTitle from '../EventTitle';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import TaskPills, { isTaskLikeEvent } from '../TaskPills';

type Props = {
    useAdminApi?: boolean;
    day: dayjs.Dayjs;
    inx: number;
    events: Event[];
    onEventEdit?: (event: Event) => void;
    timezone?: string;
    onDayItemClick?: (dayString: string) => void;
    currentEditEventDayString?: string;
    currentFocusDayStr?: string;
    usePopLayer?: boolean;
    onEventUpdate?: () => void;
    allSpecialityData?: KlarityService[];
    allClients?: Prospect[];
    providerColorMap?: Record<number, string>;
    targetUrlEventId?: string;
    viewStartHour?: number;
    droppableDayKey?: string;
    onEditRefillTask?: (item: any) => void;
};

const DraggableWeekEvent: React.FC<{
    evt: EventWithTimeOverlap;
    dayKey: string;
    className: string;
    style: React.CSSProperties;
    children: React.ReactNode;
    disabled?: boolean;
    onClick?: (e: React.MouseEvent) => void;
}> = ({ evt, dayKey, className, style, children, disabled, onClick }) => {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `week_evt_${evt.id}`,
        data: { event: evt, dayKey },
        disabled,
    });

    const dragStyle: React.CSSProperties = {
        ...style,
        // IMPORTANT: Don't apply transform to the original absolute-positioned event card.
        // We move a separate DragOverlay instead (Google Calendar-like), which avoids rendering "smears".
        transform: undefined,
        transition: undefined,
        cursor: disabled ? style.cursor : 'grab',
    };

    return (
        <div
            ref={setNodeRef}
            className={isDragging ? `${className} ${s.eventDragging}` : className}
            style={dragStyle}
            onClick={onClick}
            {...attributes}
            {...(!disabled ? listeners : {})}
        >
            {children}
        </div>
    );
};

const WeekDay = (props: Props) => {
    const {
        day,
        events,
        onEventEdit,
        currentEditEventDayString,
        usePopLayer = true,
        useAdminApi,
        timezone,
        onDayItemClick,
        onEventUpdate,
        allSpecialityData,
        allClients,
        providerColorMap,
        targetUrlEventId,
        viewStartHour = 0,
        droppableDayKey,
        onEditRefillTask,
    } = props;
    const [colors] = useCalendarColors({ useAdminApi });

    const [currentEvent, setCurrentEvent] = React.useState<Event>();
    const [showEdit, setShowEdit] = React.useState(false);

    const hasAutoPop = React.useRef(false);

    const isCurrentDay = day.format('DD-MM-YY') === dayjs().format('DD-MM-YY');
    const eventsWithTimeOverlap: EventWithTimeOverlap[] = React.useMemo(() => {
        const ret: EventWithTimeOverlap[] = [];
        for (let i = 0; i < events.length; i++) {
            const _currentEvent = events[i];
            const timeOverlapKeys: string[] = [];
            for (let j = 0; j < i; j++) {
                const event2 = events[j];
                if (isEventTimeOverlap(_currentEvent, event2)) {
                    timeOverlapKeys.push(event2.id);
                }
            }
            ret.push({ ...events[i], timeOverlapKeys });
        }
        return ret;
    }, [events]);

    const taskEvents = React.useMemo(() => {
        return (eventsWithTimeOverlap || []).filter(isTaskLikeEvent);
    }, [eventsWithTimeOverlap]);

    const timedEvents = React.useMemo(() => {
        return (eventsWithTimeOverlap || []).filter((e) => !isTaskLikeEvent(e));
    }, [eventsWithTimeOverlap]);

    React.useEffect(() => {
        if (!currentEditEventDayString || currentEditEventDayString !== day.format('DD-MM-YY')) {
            setCurrentEvent(undefined);
            setShowEdit(false);
        }
    }, [currentEditEventDayString, day]);

    React.useEffect(() => {
        if (targetUrlEventId && events) {
            const target = events.find((item) => `${item.id}` === targetUrlEventId);
            if (target) {
                // if (onEventEdit) {
                //     onEventEdit(target);
                //     return;
                // }
                setTimeout(() => {
                    if (hasAutoPop.current) {
                        return;
                    }
                    hasAutoPop.current = true;
                    // if (onEventEdit) {
                    //     onEventEdit(target);
                    //     return;
                    // }
                    setCurrentEvent(target);
                    setShowEdit(true);
                    onDayItemClick?.(day.format('DD-MM-YY'));
                }, 2500);
            }
        }
    }, [targetUrlEventId, events]);

    const pendingAreaContent = () => {
        if (!showEdit) {
            return null;
        }
        return (
            <div className={s.areaWrap}>
                <AddAndEditCalendarEvent
                    onCancel={() => {
                        setCurrentEvent(undefined);
                        setShowEdit(false);
                    }}
                    onSuccess={() => {
                        setCurrentEvent(undefined);
                        setShowEdit(false);
                        onEventUpdate?.();
                    }}
                    event={currentEvent}
                    timezone={timezone}
                    isPopLayer
                    allSpecialityData={allSpecialityData}
                />
                <div
                    onClick={() => {
                        setCurrentEvent(undefined);
                        setShowEdit(false);
                    }}
                    className={s.closeIcon}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                            d="M6 18L18 6M6 6L18 18"
                            stroke="#9CA3AF"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </div>
        );
    };

    // console.log({
    //     day: day.format('DD-MM-YY'),
    //     eventsWithTimeOverlap,
    // })

    const dayKey = droppableDayKey || day.format('YYYY-MM-DD');
    const { setNodeRef: setDroppableRef, isOver } = useDroppable({
        id: `week_day_${dayKey}`,
        data: { dayKey },
    });

    return (
        <div className={s.wrap} ref={setDroppableRef} data-over={isOver ? '1' : '0'}>
            <div className={s.itemWrap}>
                <div className={s.header}>
                    <div>{day.format('ddd').toUpperCase()}</div>
                    <div className={s.headerDate}>
                        <span className={isCurrentDay ? `${s.dayLabel} ${s.today}` : s.dayLabel}>
                            {day.format('DD')}
                        </span>
                    </div>
                    {taskEvents.length > 0 && (
                        <div className={s.headerTasks} onClick={(e) => e.stopPropagation()}>
                            <TaskPills
                                events={taskEvents}
                                onEventUpdate={onEventUpdate}
                                onEdit={onEditRefillTask}
                                placement="right"
                                pillClassName={s.taskPill}
                                getPopupContainer={() => document.body}
                                as="button"
                            />
                        </div>
                    )}
                </div>
                <div className={s.content}>
                    <div className={s.eventWrap} onClick={() => {}}>
                        {timedEvents.map((evt) => {
                            let startTimeDayjs = dayjs(evt.start.dateTime);
                            let endTimeDayjs = dayjs(evt.end.dateTime);
                            if (timezone) {
                                startTimeDayjs = startTimeDayjs.tz(timezone);
                                endTimeDayjs = endTimeDayjs.tz(timezone);
                            }

                            let timeLabel = `${startTimeDayjs.format('hh:mm')} - ${endTimeDayjs.format('hh:mm A')}`;
                            if (startTimeDayjs.hour() === 0) {
                                //12 AM
                                timeLabel = `${startTimeDayjs.format('HH:mm')} - ${endTimeDayjs.format('hh:mm A')}`;
                            }

                            // Compute position based on real timestamps, clamped to the visible day range.
                            // Avoids timezone/cross-day payload quirks that can place events at wrong hours.
                            // Build the day "anchor" in the same timezone as events.
                            // IMPORTANT: use dayjs.tz(dayKey, timezone) (not dayjs(dayKey).tz(timezone)),
                            // otherwise the date can shift and place events at wrong hours.
                            let viewStart = timezone ? dayjs.tz(dayKey, timezone) : dayjs(dayKey);
                            viewStart = viewStart.hour(viewStartHour).minute(0).second(0).millisecond(0);
                            const viewEnd = viewStart.endOf('day');

                            const clampStart = startTimeDayjs.isBefore(viewStart) ? viewStart : startTimeDayjs;
                            const clampEnd = endTimeDayjs.isAfter(viewEnd) ? viewEnd : endTimeDayjs;

                            const topMin = Math.max(0, clampStart.diff(viewStart, 'minute'));
                            const heightMin = Math.max(0, clampEnd.diff(clampStart, 'minute'));
                            const top = (topMin * 50) / 60;

                            const color = getEventColor(evt, colors);
                            const overlapCount = evt.timeOverlapKeys.length;
                            let height = heightMin > 0 ? (heightMin * 50) / 60 : 0;
                            if (height <= 0) {
                                // Same-time or invalid duration; render a minimum height so it's still clickable.
                                height = 20;
                            }
                            let narrowLayout = false;
                            if (height > 0 && height < 15) {
                                narrowLayout = true;
                            }
                            if (height > 15 && height < 17) {
                                height = 22;
                            }
                            let smallLine = false;
                            if (height > 25 && height < 40) {
                                smallLine = true;
                            }
                            const toolContent = (
                                <>
                                    <div className={s.toolTextTitle}>
                                        <EventTitle event={evt} allSpecialityData={allSpecialityData} />
                                    </div>
                                    <div className={s.tooltTextTime}>{timeLabel}</div>
                                </>
                            );
                            let className = narrowLayout ? `${s.event} ${s.narrowEvent}` : s.event;
                            if (evt.type === 'blocker') {
                                className = `${className} ${s.eventBlocker}`;
                            }
                            if (isCancelledEvent(evt)) {
                                className = `${className} ${s.eventCancelled}`;
                            }
                            if (smallLine) {
                                className = `${className} ${s.eventSmallLine}`;
                            }
                            const isEditing = currentEvent && currentEvent.id === evt.id && showEdit;

                            const cssColor = getEventColorSetting(evt, allClients, providerColorMap);
                            // Legacy guard no longer needed: top/height are duration-based and clamped above.
                            if (isEditing) {
                                return (
                                    <Popover
                                        key={evt.id}
                                        open
                                        zIndex={1000}
                                        placement="right"
                                        title={null}
                                        content={pendingAreaContent()}
                                        arrow
                                    >
                                        <div
                                            className={`${className} weekEventItem`}
                                            id={evt.id ? `app_${evt.id}` : undefined}
                                            style={{
                                                top,
                                                height: `${height}px`,
                                                right: 8,
                                                left: overlapCount * 20,
                                                color: cssColor.color,
                                                backgroundColor: cssColor.backgroundColor,
                                                border: `0.5px ${cssColor.borderStyle} ${cssColor.borderColor}`,
                                            }}
                                        >
                                            <p className={s.title}>
                                                <Tooltip
                                                    autoAdjustOverflow
                                                    color="#fff"
                                                    overlayClassName={s.toolText}
                                                    placement="top"
                                                    title={toolContent}
                                                >
                                                    <EventTitle event={evt} allSpecialityData={allSpecialityData} />
                                                    {/* {getEventTitleDisplay(evt) || 'No title'} */}
                                                </Tooltip>
                                            </p>
                                            <p className={s.time}>{timeLabel}</p>
                                        </div>
                                    </Popover>
                                );
                            }
                            const isDraggable =
                                evt.type !== 'task' &&
                                !isCancelledEvent(evt) &&
                                // Prevent dragging extremely long / cross-day events (they render oddly and can be confusing).
                                startTimeDayjs.isSame(endTimeDayjs, 'day');
                            return (
                                <DraggableWeekEvent
                                    key={evt.id}
                                    evt={evt}
                                    dayKey={dayKey}
                                    disabled={!isDraggable}
                                    className={`${className} ${evt.isSample ? 'weekEventItemTourTip' : 'weekEventItem'}`}
                                    style={{
                                        top,
                                        height: `${height}px`,
                                        right: 8,
                                        left: overlapCount * 20,
                                        color: cssColor.color,
                                        backgroundColor: cssColor.backgroundColor,
                                        border: `0.5px ${cssColor.borderStyle} ${cssColor.borderColor}`,
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (usePopLayer) {
                                            setCurrentEvent(evt);
                                            setShowEdit(true);
                                            onDayItemClick?.(day.format('DD-MM-YY'));
                                        } else if (onEventEdit) {
                                            onEventEdit(evt);
                                        }
                                    }}
                                >
                                    <p className={s.title}>
                                        <Tooltip
                                            autoAdjustOverflow
                                            color="#fff"
                                            overlayClassName={s.toolText}
                                            placement="top"
                                            title={toolContent}
                                        >
                                            <EventTitle event={evt} allSpecialityData={allSpecialityData} />
                                        </Tooltip>
                                    </p>
                                    <p className={s.time}>{timeLabel}</p>
                                </DraggableWeekEvent>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WeekDay;
